import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import EditWorkerProfileModal from '../components/EditWorkerProfileModal';
import EditBuyerProfileModal from '../components/EditBuyerProfileModal';
import loadImage from '../assets/imageLoader';
import apiClient from '../api/api';
import { useAuth } from '../context/AuthContext';

const UserProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: authUser } = useAuth(); // Logged-in user
  const [userProfile, setUserProfile] = useState(null); // The profile being viewed
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showWorkerEditModal, setShowWorkerEditModal] = useState(false);
  const [showBuyerEditModal, setShowBuyerEditModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Determine if the logged-in user is viewing their own profile
  const isOwnProfile = authUser?.id === parseInt(userId);

  const fetchUserProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // This endpoint should return the user with Profile and Worker/Buyer details included
      const response = await apiClient.get(`/profiles/${userId}`);
      setUserProfile(response.data);
    } catch (err) {
      setError("Failed to load user profile. The backend might be offline or the endpoint doesn't exist yet.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  const handleSaveWorker = async (updatedData) => {
    setIsSaving(true);
    setError(null);
    try {
      await apiClient.put(`/profiles/worker/${userId}`, updatedData);
      setShowWorkerEditModal(false);
      await fetchUserProfile();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save profile. Please try again.');
      console.error('Failed to save worker profile:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveBuyer = async (updatedData) => {
    setIsSaving(true);
    setError(null);
    try {
      await apiClient.put(`/profiles/buyer/${userId}`, updatedData);
      setShowBuyerEditModal(false);
      await fetchUserProfile();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save profile. Please try again.');
      console.error('Failed to save buyer profile:', err);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center p-10">Loading user profile...</div>;
  }

  // Determine if modal is open to prevent page-level error from showing
  const isModalOpen = showWorkerEditModal || showBuyerEditModal;
  if (error && !isModalOpen) {
    return <div className="text-center p-10 text-red-500">{error}</div>;
  }

  if (!userProfile) {
    return <div className="text-center p-10">User not found.</div>;
  }

  // Destructure for easier access in JSX, assuming backend sends this structure
  const { profile, worker_details, buyer_details } = userProfile;
  const isWorker = !!worker_details;
  const isBuyer = !!buyer_details;

  return (
    <div className="container mx-auto p-4 sm:p-6 lg:p-8">
      <h1 className="text-3xl font-bold mb-8 text-center text-gray-800">User Profile</h1>
      <div className="bg-white shadow-xl rounded-lg p-6 md:p-8 max-w-4xl mx-auto">
        <div className="flex flex-col md:flex-row items-center md:items-start mb-8 border-b pb-6">
          <img
            src={loadImage(profile?.picture)}
            alt={profile?.name}
            className="w-32 h-32 rounded-full object-cover border-4 border-green-500 shadow-md mb-4 md:mb-0 md:mr-6"
          />
          <div className="text-center md:text-left">
            <h2 className="text-3xl font-bold text-gray-900">{profile?.name}</h2>
            {isWorker && <p className="text-green-600 text-lg">{worker_details?.preferred_working_category?.join(', ') || 'N/A'}</p>}
            {isBuyer && <p className="text-blue-600 text-lg">{buyer_details?.company_name || 'Individual Buyer'}</p>}

            {isOwnProfile && (
              <button
                className="mt-4 bg-blue-600 text-white px-8 py-3 rounded-full hover:bg-blue-700 transition duration-300 disabled:bg-gray-400"
                onClick={() => isWorker ? setShowWorkerEditModal(true) : setShowBuyerEditModal(true)}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Edit Profile'}
              </button>
            )}

            {!isOwnProfile && (
              <button
                onClick={() => navigate('/messages', { state: { startChatWith: { id: profile.id, name: profile.name, picture: profile.picture } } })}
                className="mt-4 bg-teal-600 text-white px-8 py-3 rounded-full hover:bg-teal-700 transition duration-300 shadow-md flex items-center justify-center md:justify-start"
              >
                Message
              </button>
            )}
          </div>
        </div>

        {error && isModalOpen && <p className="text-red-500 text-center my-4">{error}</p>}

        {/* Other sections would be updated similarly... */}
        {/* For brevity, we'll assume the backend provides the necessary details */}

      </div>
      {isOwnProfile && isWorker && (
        <EditWorkerProfileModal
          showModal={showWorkerEditModal}
          onClose={() => setShowWorkerEditModal(false)}
          workerData={userProfile}
          onSave={handleSaveWorker}
        />
      )}
      {isOwnProfile && isBuyer && (
        <EditBuyerProfileModal
          showModal={showBuyerEditModal}
          onClose={() => setShowBuyerEditModal(false)}
          buyerData={userProfile}
          onSave={handleSaveBuyer}
        />
      )}
    </div>
  );
};

export default UserProfilePage;
